import { readPersistedAuthState } from '@cognite/e2e-auth';

import {
  buildAppPath,
  parseAppPathFromPathname,
} from '../features/routing/project-path';

import type { FrontendRouterWindow } from './apply-infield-frontend-router-setup';

export type ProjectPathWindow = FrontendRouterWindow & {
  localStorage: Pick<Storage, 'getItem'>;
};

const isOidcCallback = (search: string): boolean => {
  const params = new URLSearchParams(search);
  return params.has('code') || params.has('state');
};

const readStoredOrgAndProject = (
  storage: Pick<Storage, 'getItem'>
): { org: string; project: string } | undefined => {
  const persisted = readPersistedAuthState(storage);
  const org = persisted?.projectInfo.org;
  const project = persisted?.projectInfo.id;
  if (!org || !project) {
    return undefined;
  }

  return { org, project };
};

/**
 * Rewrites legacy paths (without org/project prefix) to `/{org}/{project}/...`
 * when a known org and project are available in an active session in
 * localStorage. Skips OIDC callback URLs.
 */
export const applyInfieldProjectPathSetup = (
  projectPathWindow: ProjectPathWindow
): void => {
  if (isOidcCallback(projectPathWindow.location.search)) {
    return;
  }

  const { isLegacyPath } = parseAppPathFromPathname(
    projectPathWindow.location.pathname
  );
  if (!isLegacyPath) {
    return;
  }

  const storedContext = readStoredOrgAndProject(projectPathWindow.localStorage);
  if (!storedContext) {
    return;
  }

  try {
    const nextPath = buildAppPath(
      storedContext.org,
      storedContext.project,
      projectPathWindow.location.pathname
    );
    const nextUrl = new URL(projectPathWindow.location.origin);
    nextUrl.pathname = nextPath;
    nextUrl.search = projectPathWindow.location.search;
    nextUrl.hash = projectPathWindow.location.hash;
    projectPathWindow.history.replaceState(null, '', nextUrl.toString());
  } catch (error) {
    console.error(
      'Failed to rewrite the URL with the stored org and project',
      error
    );
  }
};
